import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import { Deal } from '../types/deal';
import { useDealStagesStore } from '../store/dealStagesStore';
import { useOwnersStore } from '../store/ownersStore';
import { APTY_PARTNER } from '../constants';
import { useAuth } from 'react-oidc-context';

interface DealTableProps {
  deals: Deal[];
  onEdit: (deal: Deal) => void;
  onDelete: (id: string) => void;
}

export const DealTable: React.FC<DealTableProps> = ({ deals, onEdit, onDelete }) => {
  const [searchTerm] = useState('');
  const { dealStages, loading: stagesLoading } = useDealStagesStore();
  const { currentPartnerName, getOwnerName } = useOwnersStore();
  const auth = useAuth();

  useEffect(() => {
    deals.forEach(deal => {
      getOwnerName(deal.properties.hubspot_owner_id, auth.user?.access_token || ''); 
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals]);

  const getDealStageName = (stageId: string) => {
    if (stagesLoading) return 'Loading...';
    return dealStages[stageId] || stageId;
  };

  const getDealStageClassName = (stageId: string) => {
    switch (dealStages[stageId]) {
      case 'Discovery - Meeting':
        return 'discovery';
      case 'Understand':
        return 'understand';
      case 'Educate':
        return 'educate';
      case 'Propose':
        return 'propose';
      case 'POC':
        return 'poc';
      case 'Closed Won':
        return 'closed-won';
      case 'Closed Lost':
        return 'closed-lost';
      case 'Vendor of Choice':
        return 'vendor';
      case 'Contract':
        return 'contract';  
    }
  };

  const filteredDeals = deals.filter((deal) => {
    if (!searchTerm) return true;
    
    const searchValue = searchTerm.toLowerCase();
    const {
      dealname,
      amount,
      number_of_applications__c,
      number_of_users__c,
      focus_application,
      dealstage,
      partner_sales_rep_name
    } = deal.properties;

    // Search in all fields
    return (
      dealname.toLowerCase().includes(searchValue) ||
      getDealStageName(dealstage).toLowerCase().includes(searchValue) ||
      (number_of_applications__c || '').toLowerCase().includes(searchValue) ||
      (focus_application || '').toLowerCase().includes(searchValue) ||
      (number_of_users__c || '').toLowerCase().includes(searchValue) ||
      (amount?.toString() || '').toLowerCase().includes(searchValue) ||
      (partner_sales_rep_name || '').toLowerCase().includes(searchValue)
    );
  });

  const formatAmount = (amount: string | number) => {
    if (!amount) return '$0';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  return (
    <div className='table-container'>
      {/* <TextField
        label="Search Deals (all fields)"
        placeholder="Search in any field..."
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      /> */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {currentPartnerName === APTY_PARTNER && <TableCell>Partner</TableCell>}
              <TableCell>Deal Name</TableCell>
              <TableCell>Deal Stage</TableCell>
              <TableCell>#Applications</TableCell>
              <TableCell>Application Name(s)</TableCell>
              <TableCell>#Users</TableCell>
              <TableCell>$Amount</TableCell>
              <TableCell>Sales Executive</TableCell>
              {/* <TableCell>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDeals.map((deal) => (
              <TableRow key={deal.id}>
                {currentPartnerName === APTY_PARTNER && <TableCell>{deal.properties.associated_partner || '--'}</TableCell>}
                <TableCell>
                  <Tooltip title={deal.properties.dealname}>
                    <Typography noWrap sx={{ maxWidth: 150 }} onClick={() => onEdit(deal)} style={{ cursor: 'pointer', fontStyle: 'underline' }}>
                      {deal.properties.dealname}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip  className={`status ${getDealStageClassName(deal.properties.dealstage)}`}
                    label={getDealStageName(deal.properties.dealstage)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>{deal.properties.number_of_applications__c}</TableCell>
                <TableCell>{deal.properties.focus_application === 'Other' ? deal.properties.focus_application_name__other_ || 'Other' : deal.properties.focus_application}</TableCell>
                <TableCell>{deal.properties.number_of_users__c}</TableCell>
                <TableCell>{formatAmount(deal.properties.amount)}</TableCell>
                <TableCell>{deal.properties.partner_sales_rep_name || 'Loading...'}</TableCell>
                {/* <TableCell className='actions-cell'>
                  <IconButton className='edit-btn' onClick={() => onEdit(deal)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => onDelete(deal.id)}>
                    <Delete />
                  </IconButton>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}; 
